import { Component, inject, OnInit, signal, WritableSignal } from '@angular/core';

import { ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { TaxObjectItemForm } from './forms/tax-object-item.form';
import { StagesComponent } from '../../../../../common/stages/stages.component';
import { SpinnerComponent } from '../../../../../common/spinner/spinner.component';
import { AccordionGroupComponent } from '../../../../../common/accordion-group/accordion-group.component';
import { AlertComponent } from '../../../../../common/alert/alert.component';
import { AccordionItemComponent } from '../../../../../common/accordion-item/accordion-item.component';
import { ApplicationCommonComponent } from '../application.common.component';
import { ApplicationAppEntityApplicationRead, TaxObjectAppEntityTaxObjectWrite } from '../../../../../openapi';
import { updateTaxObjectsData } from '../../../../../store/actions/application.actions';
import { TaxObjectItemFormComponent } from './tax-object-item-form/tax-object-item-form.component';
import { TaxObjectItemPreviewComponent } from './tax-object-item-preview/tax-object-item-preview.component';
import { TaxObjectTitlePipe } from '../../../../../pipes/tax-object-title.pipe';

export interface TaxObjectDataComponentCustomTitles {
  cardTitle: string;
  cardTitle2: string;
  addBtnTitle: string;
  addBtn2Title: string;
  noDataInfo: string;
}

export interface TaxObjectDataComponentConfig {
  stages: string[];
  successUrl: string;
  cancelUrl: string;
  forceNext?: boolean;
  customTitles?: TaxObjectDataComponentCustomTitles;
  removeIsPartOfNatureReserveControl?: boolean;
  removeIsTaxExemptControl?: boolean;
  removeAreaHaControl?: boolean;
  addTaxExemptTypeControl?: boolean;
  addGroundTypeControl?: boolean;
  addUsageClassControl?: boolean;
  addPropertyTypeControl?: boolean;
  addAreaTypeControl?: boolean;
}

@Component({
  selector: 'app-tax-object-data-section',
  standalone: true,
  imports: [
    StagesComponent,
    SpinnerComponent,
    ReactiveFormsModule,
    RouterLink,
    TaxObjectItemFormComponent,
    AccordionGroupComponent,
    AlertComponent,
    AccordionItemComponent,
    TaxObjectItemPreviewComponent,
    TaxObjectTitlePipe
  ],
  providers: [TaxObjectItemForm],
  templateUrl: './tax-object-data-section.component.html'
})
export class TaxObjectDataSectionComponent extends ApplicationCommonComponent implements OnInit {

  public stages: string[] = this.route.snapshot.data['stages'];
  public successUrl: string = this.route.snapshot.data['successUrl'];
  public cancelUrl: string = this.route.snapshot.data['cancelUrl'];
  public forceNext: boolean = !!this.route.snapshot.data['forceNext'];

  public customTitles: TaxObjectDataComponentCustomTitles | undefined = this.route.snapshot.data['customTitles'];

  public form: TaxObjectItemForm = inject(TaxObjectItemForm);

  public taxObjects: WritableSignal<TaxObjectAppEntityTaxObjectWrite[]> = signal<TaxObjectAppEntityTaxObjectWrite[]>([]);

  public currentIndex: WritableSignal<number | undefined> = signal(undefined);

  public showItemForm: boolean = false;

  public override onFetchApplicationData = (app: ApplicationAppEntityApplicationRead | null) => {
    this.taxObjects.set((app?.taxObjects?.map(v => ({
      ...v,
      areaHa: v?.areaHa
    })) || []) as TaxObjectAppEntityTaxObjectWrite[]);
  };

  public onUpdateTaxObjectData(forest: TaxObjectAppEntityTaxObjectWrite): void {
    if (this.currentIndex() !== undefined) {
      this.taxObjects.update(v => {
        const arr = [...v];
        arr[this.currentIndex() || 0] = forest;
        return arr;
      });
      this.currentIndex.set(undefined);
    } else {
      this.taxObjects.update(v => ([
        ...v,
        forest
      ]));
    }
    this.showItemForm = false;
    this.form.reset();
  }

  public onAdd(): void {
    this.showItemForm = true;
    this.currentIndex.set(undefined);
  }

  public onEditTaxObject(event: Event, i: number): void {
    event.preventDefault();
    event.stopPropagation();
    this.currentIndex.set(i);
    this.showItemForm = true;
    this.form.patchFormValues({
      ...this.taxObjects()[i]
    });
  }

  public onRemove(i: number, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.taxObjects.update(v => {
      v.splice(i, 1);
      return v;
    });
  }

  public submitAndGoNext(): void {
    if (this.taxObjects().length) {
      this.store$.dispatch(
        updateTaxObjectsData({
          application: this.application() || {},
          taxObjects: this.taxObjects(),
          successFnc: () => {
            this.goNext();
          },
          failureFnc: () => {
          }
        })
      );
    }
  }

  public goNext(): void {
    this.router.navigate([this.successUrl], { relativeTo: this.route });
  }

  public override ngOnInit() {
    super.ngOnInit();
    if (this.route.snapshot.data['removeIsPartOfNatureReserveControl']) {
      this.form.removeControl('isPartOfNatureReserve');
    }
    if (this.route.snapshot.data['removeIsTaxExemptControl']) {
      this.form.removeControl('isTaxExempt');
    }
    if (this.route.snapshot.data['removeAreaHaControl']) {
      this.form.removeControl('areaHa');
    }
    if (this.route.snapshot.data['addTaxExemptTypeControl']) {
      this.form.createTaxExemptTypeControl();
    }
    if (this.route.snapshot.data['addGroundTypeControl']) {
      this.form.createGroundTypeControl();
    }
    if (this.route.snapshot.data['addUsageClassControl']) {
      this.form.createUsageClassControl();
    }
    if (this.route.snapshot.data['addPropertyTypeControl']) {
      this.form.createPropertyTypeControl();
    }
    if (this.route.snapshot.data['addAreaTypeControl']) {
      this.form.createAreaTypeControl();
    }
  }
}
